import { ProjectMessageFile } from "../../app/services/projectMessages";

// フォルダノードの型定義
export interface FolderNode {
  id: string;
  name: string;
  parentId: string | null;
  files: ProjectMessageFile[];
  children: FolderNode[];
  depth: number; // 階層の深さ（0から始まる）
}

// フォルダ情報の型定義
interface FolderInfo {
  id: string;
  name: string;
  parentId: string | null;
}

/**
 * ファイル配列からフォルダ情報を抽出する
 */
const extractFolders = (files: ProjectMessageFile[]): Map<string, FolderInfo> => {
  const folders = new Map<string, FolderInfo>();
  
  files.forEach(file => {
    if (file.folder) {
      folders.set(file.folder.id, {
        id: file.folder.id,
        name: file.folder.name,
        parentId: file.folder.parent_id,
      });
    }
  });
  
  return folders;
};

const compareByFileName = (a: ProjectMessageFile, b: ProjectMessageFile): number =>
  a.file_name.localeCompare(b.file_name, 'ja');

/**
 * ファイル配列からツリー構造を構築する
 *
 * 返却される各配列は日本語ロケールで名前昇順にソート済み:
 * - `rootFolders` および各階層の `children`: フォルダ名で昇順
 * - 各フォルダの `files`: `file_name` で昇順
 * - `filesWithoutFolder`: `file_name` で昇順
 *
 * @param files - ファイル配列
 * @param maxDepth - 最大階層数（デフォルト: 5）
 * @returns ルートフォルダノードの配列とフォルダなしファイルの配列
 */
export const buildFolderTree = (
  files: ProjectMessageFile[],
  maxDepth: number = 5
): { rootFolders: FolderNode[]; filesWithoutFolder: ProjectMessageFile[] } => {
  // フォルダなしファイルを分離
  const filesWithoutFolder = files.filter(file => !file.folder);
  const filesWithFolder = files.filter(file => file.folder);
  
  // フォルダ情報を抽出
  const folderMap = extractFolders(filesWithFolder);
  
  // フォルダノードを作成
  const folderNodes = new Map<string, FolderNode>();
  
  folderMap.forEach((folderInfo, folderId) => {
    folderNodes.set(folderId, {
      id: folderId,
      name: folderInfo.name,
      parentId: folderInfo.parentId,
      files: [],
      children: [],
      depth: 0,
    });
  });
  
  // ファイルを対応するフォルダに振り分け
  filesWithFolder.forEach(file => {
    if (file.folder) {
      const folderNode = folderNodes.get(file.folder.id);
      if (folderNode) {
        folderNode.files.push(file);
      }
    }
  });
  
  // 親子関係を構築
  const rootFolders: FolderNode[] = [];
  
  folderNodes.forEach(folderNode => {
    if (folderNode.parentId === null) {
      // ルートフォルダ
      folderNode.depth = 0;
      rootFolders.push(folderNode);
    } else {
      // 親フォルダがあれば子として追加
      const parentNode = folderNodes.get(folderNode.parentId);
      if (parentNode) {
        parentNode.children.push(folderNode);
      } else {
        // 親フォルダが見つからない場合はルートとして扱う
        folderNode.depth = 0;
        rootFolders.push(folderNode);
      }
    }
  });
  
  // 深さを再帰的に計算（最大深さを超えたものはフラット化）
  const calculateDepth = (node: FolderNode, currentDepth: number): void => {
    node.depth = currentDepth;
    
    if (currentDepth < maxDepth - 1) {
      node.children.forEach(child => {
        calculateDepth(child, currentDepth + 1);
      });
    } else {
      // 最大深さに達したら、子フォルダのファイルを現在のフォルダに統合
      const flattenChildren = (parent: FolderNode): void => {
        parent.children.forEach(child => {
          parent.files.push(...child.files);
          flattenChildren(child);
        });
        parent.children = [];
      };
      flattenChildren(node);
    }
  };
  
  rootFolders.forEach(root => calculateDepth(root, 0));
  
  // フォルダ名と各フォルダ内のファイル名を再帰的に昇順ソート
  const sortFoldersRecursive = (folders: FolderNode[]): void => {
    folders.sort((a, b) => a.name.localeCompare(b.name, 'ja'));
    folders.forEach(folder => {
      sortFoldersRecursive(folder.children);
      folder.files.sort(compareByFileName);
    });
  };

  sortFoldersRecursive(rootFolders);

  const sortedFilesWithoutFolder = [...filesWithoutFolder].sort(compareByFileName);

  return { rootFolders, filesWithoutFolder: sortedFilesWithoutFolder };
};

/**
 * フォルダノード内のファイル総数を取得する（子フォルダ含む）
 */
export const getTotalFileCount = (folder: FolderNode): number => {
  let count = folder.files.length;
  folder.children.forEach(child => {
    count += getTotalFileCount(child);
  });
  return count;
};

// zip 内に配置する1ファイルのエントリ
export interface FlatFileEntry {
  // zip 内の相対パス（例: "サブフォルダ/ファイル名.png"）
  path: string;
  file: ProjectMessageFile;
}

/**
 * 同一階層内で名前が衝突しないように一意な名前を返す。
 * 既に使われている場合は拡張子を保ったまま連番サフィックス " (n)" を付ける。
 */
const ensureUniqueName = (name: string, usedNames: Set<string>): string => {
  if (!usedNames.has(name)) {
    usedNames.add(name);
    return name;
  }

  const dotIndex = name.lastIndexOf('.');
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const ext = dotIndex > 0 ? name.slice(dotIndex) : '';

  let counter = 1;
  let candidate = `${base} (${counter})${ext}`;
  while (usedNames.has(candidate)) {
    counter += 1;
    candidate = `${base} (${counter})${ext}`;
  }
  usedNames.add(candidate);
  return candidate;
};

/**
 * フォルダノードを再帰的に走査し、各ファイルへ zip 内の相対パスを割り当てて
 * entries に追加する。サブフォルダ構造はパスに反映される。
 * 同一階層内でのファイル名・フォルダ名の衝突は連番サフィックスで一意化する。
 */
const walkFolderEntries = (
  node: FolderNode,
  basePath: string,
  entries: FlatFileEntry[],
): void => {
  const usedNames = new Set<string>();

  node.files.forEach(file => {
    const uniqueName = ensureUniqueName(file.file_name, usedNames);
    entries.push({
      path: basePath ? `${basePath}/${uniqueName}` : uniqueName,
      file,
    });
  });

  node.children.forEach(child => {
    const uniqueDirName = ensureUniqueName(child.name, usedNames);
    const childPath = basePath ? `${basePath}/${uniqueDirName}` : uniqueDirName;
    walkFolderEntries(child, childPath, entries);
  });
};

/**
 * フォルダノードを再帰的に走査し、各ファイルへ zip 内の相対パスを割り当てて
 * フラットな配列で返す。サブフォルダ構造はパスに反映される。
 *
 * ルートフォルダ自身の名前は zip ファイル名に使う想定のため、直下のファイルは
 * パスの先頭に付けず、サブフォルダ以降のみ階層をパスに反映する。
 */
export const flattenFolderToPaths = (folder: FolderNode): FlatFileEntry[] => {
  const entries: FlatFileEntry[] = [];

  // ルート直下を起点にする（先頭にフォルダ名を付けない）
  walkFolderEntries(folder, '', entries);

  return entries;
};

/**
 * メッセージの全添付ファイルを zip 内パス付きのフラットな配列に展開する。
 *
 * フォルダに属さないファイルは zip ルート直下に、フォルダ添付分は
 * ルートフォルダ名を先頭に付けたディレクトリ構造として展開する。
 * zip ルート階層でのファイル名・フォルダ名の衝突は連番サフィックスで一意化する。
 */
export const flattenMessageFilesToPaths = (
  files: ProjectMessageFile[],
): FlatFileEntry[] => {
  const { rootFolders, filesWithoutFolder } = buildFolderTree(files);
  const entries: FlatFileEntry[] = [];
  const usedNames = new Set<string>();

  filesWithoutFolder.forEach(file => {
    const uniqueName = ensureUniqueName(file.file_name, usedNames);
    entries.push({ path: uniqueName, file });
  });

  rootFolders.forEach(folder => {
    const uniqueDirName = ensureUniqueName(folder.name, usedNames);
    walkFolderEntries(folder, uniqueDirName, entries);
  });

  return entries;
};
